import {
  CalendarDays,
  Check,
  Clock,
  type LucideIcon,
  MessageSquare,
} from 'lucide-react';

import { UpgradeButton } from '@/modules/billing/components/UpgradeButton';

export type FreePlanSurface = 'messages' | 'interviews' | 'availability';

interface FreePlanAutomationCtaProps {
  surface: FreePlanSurface;
  /** Billing page link, including the tenant search when applicable. */
  to: string;
}

const surfaceCopy: Record<
  FreePlanSurface,
  {
    benefits: string[];
    description?: string;
    icon: LucideIcon;
    title: string;
  }
> = {
  messages: {
    icon: MessageSquare,
    title: 'Spend less time on candidate outreach',
    description:
      'Finlay handles the conversation from the first message to the pre-screen.',
    benefits: [
      'Sends LinkedIn invitations to candidates.',
      'Continues the conversation and follows up.',
      'Shares the pre-screen link with interested candidates.',
    ],
  },
  interviews: {
    icon: CalendarDays,
    title: 'Spend less time getting candidates to interview',
    benefits: [
      'Finlay reaches out and follows up with candidates on LinkedIn.',
      'Interested candidates complete a short AI pre-screen.',
      'Finlay evaluates candidates and sends strong matches for your review.',
      'You coordinate the interview with candidates you approve.',
    ],
  },
  availability: {
    icon: Clock,
    title: 'Keep your interview preferences in one place',
    description:
      'Save your interview length, timezone, and weekly availability.',
    benefits: [
      'Save the hours that usually work for you.',
      'Keep one default interview length and timezone.',
      'You coordinate interview times with candidates you approve.',
    ],
  },
};

/**
 * Replaces the empty state on Messages, Interviews and Availability for a
 * Free tenant: the page is empty because nothing is automated yet, so the
 * next step is starting automation, not creating another job. Mounted
 * through `FreePlanGate`, which decides from Stigg.
 */
export function FreePlanAutomationCta({
  surface,
  to,
}: FreePlanAutomationCtaProps) {
  const copy = surfaceCopy[surface];

  return (
    <div className="bg-muted/30 flex flex-col items-center gap-5 rounded-xl border px-6 py-8 text-center">
      <span className="bg-muted grid size-11 place-items-center rounded-xl text-neutral-600">
        <copy.icon aria-hidden className="size-5" />
      </span>
      <div className="flex max-w-md flex-col gap-1.5">
        <h3 className="text-[15px] leading-6 font-semibold tracking-[-0.01em]">
          {copy.title}
        </h3>
        {copy.description && (
          <p className="text-muted-foreground text-[13px] leading-5">
            {copy.description}
          </p>
        )}
      </div>
      <ul className="flex max-w-md flex-col gap-1.5 text-left">
        {copy.benefits.map((benefit) => (
          <li className="flex items-start gap-2 text-[13px]" key={benefit}>
            <Check
              aria-hidden
              className="text-primary-700 mt-0.5 size-3.5 shrink-0"
            />
            <span>{benefit}</span>
          </li>
        ))}
      </ul>
      <UpgradeButton label="View plans" source="hm-free-plan-cta" to={to} />
    </div>
  );
}
